import React, { useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
    GET_TRANSLATION_AI_LABEL_REQUESTS,
    GET_TRANSLATION_AI_USAGE_DAILIES,
    GET_TRANSLATION_AI_USAGE_DAILY_TOTAL
} from "graphql/query/translation-ai-gql";
import Settings from "components/settings";
import { PageWrapDefault, Tables } from "components/layout";
import { Localize } from "components/service";
import { useActiveMenuItem, useBreadCrumbs } from "components/use-hooks";
import TranslationAiTotalSummary from "./translation-ai-total-summary";
import TranslationAiLanguageCacheStatus from "./translation-ai-language-cache-status";

import './settings.scss';


const routeUrl = Settings.Const.translationAiUsagePath;
const tableHelper = Settings.Helpers.TranslationAiTable.Usage;
const languageCacheHelper = Settings.Helpers.TranslationAiLanguageCache;

const TranslationAiUsagePage = () => {

    useActiveMenuItem([ "settings-translation-ai-usage" ], [ "settings" ]);

    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Settings" }).props.children,
            path: `${ Settings.Const.basePath }`
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_TranslationAiUsage" }).props.children,
            path: `${ routeUrl }`
        }
    ];

    useBreadCrumbs(breadcrumbs);

    let match = useRouteMatch(`${ routeUrl }/page/:pageNum`);

    const [ searchText, setSearchText ] = useState("");
    const [ filters, setFilters ] = useState();
    const [ records, setRecords ] = useState([]);

    const { data, loading } = useQuery(GET_TRANSLATION_AI_USAGE_DAILY_TOTAL, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const { data: languageCacheData, loading: languageCacheLoading } = useQuery(GET_TRANSLATION_AI_LABEL_REQUESTS, {
        variables: languageCacheHelper.labelRequestVariables,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const languageCacheStatuses = useMemo(() => (
        languageCacheHelper.buildStatuses(languageCacheData?.translateAiRequests?.data || [])
    ), [ languageCacheData ]);

    return (
        <PageWrapDefault
            className="page-settings page-translation-ai-usage"
            dataExist={ true }
            title={ <Localize>PAGES.Title_TranslationAiUsage</Localize> }
        >
            <TranslationAiTotalSummary
                loading={ loading }
                total={ data?.translateAiUsageDailyTotal }
            />

            <TranslationAiLanguageCacheStatus
                loading={ languageCacheLoading }
                statuses={ languageCacheStatuses }
            />

            <div className="translation-ai-analytics-layout">
                <div className="translation-ai-table-panel">
                    <Tables.Main
                        model="translateAiUsageDailies"
                        query={ GET_TRANSLATION_AI_USAGE_DAILIES }
                        varSet={ { perPage: 25 } }
                        routeUrl={ `${ routeUrl }` }
                        currentPage={ match ? parseInt(match.params.pageNum) : 1 }
                        searchText={ searchText }
                        objFilter={ filters }
                        objOrderBy={ [ { column: "DATE_AT", order: "DESC" } ] }
                        tableHelper={ tableHelper }
                        tableLayout="auto"
                        extraClass="translation-ai-compact-table"
                        onData={ setRecords }
                    >
                        <div className="table-action-bar">
                            <div className="col">
                                <Settings.Filters.TranslationAiTable
                                    setSearchText={ setSearchText }
                                    setFilters={ setFilters }
                                    dateColumn="DATE_AT"
                                    searchPlaceholder="SEARCH.Input_Placeholder_TranslationAiUsage" />
                            </div>
                        </div>
                    </Tables.Main>
                </div>
                <div className="translation-ai-chart-panel">
                    <Settings.Charts.TranslationAi.Usage records={ records } />
                </div>
            </div>
        </PageWrapDefault>
    );
};

export default TranslationAiUsagePage;
